import styled from "@emotion/styled";
import type { MouseEventHandler, ReactNode } from "react";
import { useCallback, useState } from "react";

import { useConversion } from "../perspective";
import { TouchTarget } from "../touch";
import type { TouchPoint } from "../touch/types";
import type { Point } from "../types";
import { isHorizontalPlacement } from "./helpers";
import { useTray } from "./TrayContext";
import type { Placement } from "./types";

const TrayItemToolTip = styled.div<{ placement: Placement }>`
  position: absolute;
  bottom: 0;
  left: 50%;

  display: inline-block;
  width: max-content;

  font-weight: bold;

  opacity: 0;

  background-color: rgba(0, 0, 0, 0.08);

  border-radius: 1rem;

  padding: 8px 16px;

  transform: ${({ placement }) =>
    isHorizontalPlacement(placement)
      ? "translate(-50%, -200%)"
      : placement === "left"
        ? "translate(50px, -25%)"
        : "translate(calc(-100% - 50px), -25%)"};

  pointer-events: none;

  transition: opacity 150ms ease-out 100ms;
`;

const TrayItemContainer = styled.div<{ available: boolean; hover: boolean; placement: Placement }>`
  position: relative;
  display: flex;
  place-content: center;

  opacity: ${({ available }) => (available ? 0.35 : 0.1)};

  transition:
    opacity 300ms ease-out,
    transform 150ms ease-out;

  ${({ hover }) => hover && "opacity: 1;"}

  pointer-events: ${({ available }) => (available ? "inherit" : "none")};

  cursor: pointer;

  &:hover {
    opacity: 1;
    transform: scale(1);
  }

  &:hover ${TrayItemToolTip} {
    opacity: 1;
  }
`;

type TrayItemProps = {
  available?: boolean;
  onSelect: (position: Point) => void;
  tooltip?: ReactNode;
  children: ReactNode;
};

export const TrayItem = ({ available = true, onSelect, tooltip, children }: TrayItemProps) => {
  const { placement } = useTray();

  const { clientToMap } = useConversion();
  const [hover, setHover] = useState(false);

  const handleMouseClick: MouseEventHandler<HTMLDivElement> = useCallback((event) => event.stopPropagation(), []);

  const handleMouseUp: MouseEventHandler<HTMLDivElement> = useCallback(
    ({ clientX, clientY }) => onSelect(clientToMap({ x: clientX, y: clientY })),
    [clientToMap, onSelect],
  );

  const handleTouchMoveOn = () => setHover(true);
  const handleTouchMoveOff = () => setHover(false);

  const handleTouchEnd = useCallback(
    ({ x, y }: TouchPoint) => {
      handleMouseUp({ clientX: x, clientY: y } as React.MouseEvent<HTMLDivElement, MouseEvent>);
      setHover(false);
    },
    [handleMouseUp],
  );

  return (
    <TouchTarget onTouchMoveOn={handleTouchMoveOn} onTouchMoveOff={handleTouchMoveOff} onTouchEnd={handleTouchEnd}>
      <TrayItemContainer
        available={available}
        hover={hover}
        placement={placement}
        onMouseUp={handleMouseUp}
        onClick={handleMouseClick}
        data-test="item"
      >
        {tooltip && <TrayItemToolTip placement={placement}>{tooltip}</TrayItemToolTip>}
        {children}
      </TrayItemContainer>
    </TouchTarget>
  );
};
